/**
 * Utility functions for handling Spotify URLs including internationalized variants
 *
 * Spotify URLs can include internationalization segments like:
 * - https://open.spotify.com/track/ID (standard)
 * - https://open.spotify.com/intl-pt/track/ID (internationalized)
 */

/**
 * Regex patterns for Spotify URLs that support internationalization
 * The pattern (?:intl-[a-z]{2}\/)? matches optional internationalization prefixes like "intl-pt/"
 */
export const SPOTIFY_URL_PATTERNS = {
  // Track URL pattern - matches both standard and internationalized URLs
  TRACK: /^https:\/\/open\.spotify\.com\/(?:intl-[a-z]{2}\/)?track\/[a-zA-Z0-9]{22}(?:\?.*)?$/,

  // Track URL with capture group for extracting ID
  TRACK_WITH_CAPTURE: /https:\/\/open\.spotify\.com\/(?:intl-[a-z]{2}\/)?track\/([a-zA-Z0-9]{22})(?:\?.*)?/,

  // Album URL pattern
  ALBUM: /^https:\/\/open\.spotify\.com\/(?:intl-[a-z]{2}\/)?album\/[a-zA-Z0-9]{22}(?:\?.*)?$/,

  // Album URL with capture group for extracting ID
  ALBUM_WITH_CAPTURE: /https:\/\/open\.spotify\.com\/(?:intl-[a-z]{2}\/)?album\/([a-zA-Z0-9]{22})(?:\?.*)?/,

  // Artist URL pattern
  ARTIST: /^https:\/\/open\.spotify\.com\/(?:intl-[a-z]{2}\/)?artist\/[a-zA-Z0-9]{22}(?:\?.*)?$/,

  // Artist URL with capture group for extracting ID
  ARTIST_WITH_CAPTURE: /https:\/\/open\.spotify\.com\/(?:intl-[a-z]{2}\/)?artist\/([a-zA-Z0-9]{22})(?:\?.*)?/,

  // Playlist URL pattern
  PLAYLIST: /^https:\/\/open\.spotify\.com\/(?:intl-[a-z]{2}\/)?playlist\/[a-zA-Z0-9]{22}(?:\?.*)?$/,

  // Playlist URL with capture group for extracting ID
  PLAYLIST_WITH_CAPTURE: /https:\/\/open\.spotify\.com\/(?:intl-[a-z]{2}\/)?playlist\/([a-zA-Z0-9]{22})(?:\?.*)?/,
};

/**
 * URI patterns for Spotify URIs (these don't need internationalization support)
 */
export const SPOTIFY_URI_PATTERNS = {
  TRACK: /spotify:track:([a-zA-Z0-9]{22})(?:\?.*)?/,
  ALBUM: /spotify:album:([a-zA-Z0-9]{22})(?:\?.*)?/,
  ARTIST: /spotify:artist:([a-zA-Z0-9]{22})(?:\?.*)?/,
};

/**
 * Validates if a URL is a valid Spotify track URL (including internationalized variants)
 * @param {string} url - The URL to validate
 * @returns {boolean} - True if valid Spotify track URL
 */
export function isValidSpotifyTrackUrl(url: string): boolean {
  if (!url || typeof url !== "string")
    return false;
  return SPOTIFY_URL_PATTERNS.TRACK.test(url);
}

/**
 * Validates if a URL is a valid Spotify album URL (including internationalized variants)
 * @param {string} url - The URL to validate
 * @returns {boolean} - True if valid Spotify album URL
 */
export function isValidSpotifyAlbumUrl(url: string): boolean {
  if (!url || typeof url !== "string")
    return false;
  return SPOTIFY_URL_PATTERNS.ALBUM.test(url);
}

/**
 * Validates if a URL is a valid Spotify artist URL (including internationalized variants)
 * @param {string} url - The URL to validate
 * @returns {boolean} - True if valid Spotify artist URL
 */
export function isValidSpotifyArtistUrl(url: string): boolean {
  if (!url || typeof url !== "string")
    return false;
  return SPOTIFY_URL_PATTERNS.ARTIST.test(url);
}

/**
 * Extracts the Spotify track ID from a URL or URI
 * @param {string} url - Spotify track URL or URI
 * @returns {string|null} - The track ID if found, null otherwise
 */
export function extractSpotifyTrackId(url: string): string | null {
  if (!url || typeof url !== "string")
    return null;

  // Try URL pattern first (with internationalization support)
  const urlMatch = url.match(SPOTIFY_URL_PATTERNS.TRACK_WITH_CAPTURE);
  if (urlMatch && urlMatch[1]) {
    return urlMatch[1];
  }

  // Try URI pattern
  const uriMatch = url.match(SPOTIFY_URI_PATTERNS.TRACK);
  if (uriMatch && uriMatch[1]) {
    return uriMatch[1];
  }

  return null;
}

/**
 * Extracts the Spotify album ID from a URL or URI
 * @param {string} url - Spotify album URL or URI
 * @returns {string|null} - The album ID if found, null otherwise
 */
export function extractSpotifyAlbumId(url: string): string | null {
  if (!url || typeof url !== "string")
    return null;

  // Try URL pattern first (with internationalization support)
  const urlMatch = url.match(SPOTIFY_URL_PATTERNS.ALBUM_WITH_CAPTURE);
  if (urlMatch && urlMatch[1]) {
    return urlMatch[1];
  }

  // Try URI pattern
  const uriMatch = url.match(SPOTIFY_URI_PATTERNS.ALBUM);
  if (uriMatch && uriMatch[1]) {
    return uriMatch[1];
  }

  return null;
}

/**
 * Extracts the Spotify artist ID from a URL or URI
 * @param {string} url - Spotify artist URL or URI
 * @returns {string|null} - The artist ID if found, null otherwise
 */
export function extractSpotifyArtistId(url: string): string | null {
  if (!url || typeof url !== "string")
    return null;

  // Try URL pattern first (with internationalization support)
  const urlMatch = url.match(SPOTIFY_URL_PATTERNS.ARTIST_WITH_CAPTURE);
  if (urlMatch && urlMatch[1]) {
    return urlMatch[1];
  }

  // Try URI pattern
  const uriMatch = url.match(SPOTIFY_URI_PATTERNS.ARTIST);
  if (uriMatch && uriMatch[1]) {
    return uriMatch[1];
  }

  return null;
}

/**
 * Legacy function name for backward compatibility
 * @deprecated Use extractSpotifyTrackId instead
 */
export function parseSpotifyUrl(url: string): string | null {
  return extractSpotifyTrackId(url);
}

/**
 * Validates if a URL is a valid Spotify playlist URL (including internationalized variants)
 * @param {string} url - The URL to validate
 * @returns {boolean} - True if valid Spotify playlist URL
 */
export function isValidSpotifyPlaylistUrl(url: string): boolean {
  if (!url || typeof url !== "string")
    return false;
  return SPOTIFY_URL_PATTERNS.PLAYLIST.test(url);
}

/**
 * Extracts the Spotify playlist ID from a URL
 * @param {string} url - Spotify playlist URL
 * @returns {string|null} - The playlist ID if found, null otherwise
 */
export function extractSpotifyPlaylistId(url: string): string | null {
  if (!url || typeof url !== "string")
    return null;

  const urlMatch = url.match(SPOTIFY_URL_PATTERNS.PLAYLIST_WITH_CAPTURE);
  if (urlMatch && urlMatch[1]) {
    return urlMatch[1];
  }

  return null;
}
